/**
 * VirtualKeyboardContext - Gestion globale du clavier virtuel tactile
 *
 * Ce contexte permet d'afficher automatiquement un clavier virtuel AZERTY
 * lorsqu'un champ de saisie reçoit le focus sur un écran tactile.
 */
import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

interface KeyboardPosition {
  x: number;
  y: number;
}

interface VirtualKeyboardContextType {
  isOpen: boolean;
  inputRef: HTMLInputElement | HTMLTextAreaElement | null;
  position: KeyboardPosition;
  inputType: 'text' | 'email' | 'tel' | 'number' | 'search';
  openKeyboard: (input: HTMLInputElement | HTMLTextAreaElement) => void;
  closeKeyboard: () => void;
  handleKey: (key: string) => void;
}

const VirtualKeyboardContext = createContext<VirtualKeyboardContextType | null>(null);

export function useVirtualKeyboard() {
  const context = useContext(VirtualKeyboardContext);
  if (!context) {
    throw new Error('useVirtualKeyboard must be used within VirtualKeyboardProvider');
  }
  return context;
}

// Hook optionnel qui ne throw pas si hors du provider
export function useVirtualKeyboardOptional() {
  return useContext(VirtualKeyboardContext);
}

interface VirtualKeyboardProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function VirtualKeyboardProvider({ children, enabled = true }: VirtualKeyboardProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<KeyboardPosition>({ x: 0, y: 0 });
  const [inputType, setInputType] = useState<'text' | 'email' | 'tel' | 'number' | 'search'>('text');
  const inputRefState = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Position par défaut : coin inférieur droit (au-dessus de la zone panier).
  // Si l'utilisateur a déplacé le clavier précédemment, on restaure sa position.
  const calculatePosition = useCallback(() => {
    const keyboardHeight = 680;
    const keyboardWidth = 1200;
    const padding = 16;

    // Restaurer la dernière position choisie par l'utilisateur (si elle existe et est encore visible)
    try {
      const saved = localStorage.getItem('virtualKeyboardPosition');
      if (saved) {
        const pos = JSON.parse(saved);
        if (
          typeof pos.x === 'number' &&
          typeof pos.y === 'number' &&
          pos.x >= 0 &&
          pos.y >= 0 &&
          pos.x + keyboardWidth <= window.innerWidth &&
          pos.y + keyboardHeight <= window.innerHeight
        ) {
          return pos;
        }
      }
    } catch {
      // Ignore JSON/storage errors and fall back to default
    }

    return {
      x: Math.max(padding, window.innerWidth - keyboardWidth - padding),
      y: Math.max(padding, window.innerHeight - keyboardHeight - padding),
    };
  }, []);

  const openKeyboard = useCallback((input: HTMLInputElement | HTMLTextAreaElement) => {
    if (!enabled) return;

    inputRefState.current = input;
    const pos = calculatePosition();
    setPosition(pos);

    // Déterminer le type d'input
    const type = input.getAttribute('type') || 'text';
    const inputMode = input.getAttribute('inputMode');

    if (type === 'email' || inputMode === 'email') {
      setInputType('email');
    } else if (type === 'tel' || inputMode === 'tel') {
      setInputType('tel');
    } else if (type === 'number' || inputMode === 'numeric' || inputMode === 'decimal') {
      setInputType('number');
    } else if (type === 'search') {
      setInputType('search');
    } else {
      setInputType('text');
    }

    setIsOpen(true);
  }, [enabled, calculatePosition]);

  const closeKeyboard = useCallback(() => {
    setIsOpen(false);
    inputRefState.current = null;
  }, []);

  const handleKey = useCallback((key: string) => {
    const input = inputRefState.current;
    if (!input) return;

    // <input type="number"> ne supporte pas selectionStart/End dans WebKit
    // (renvoie null) — sans fallback chaque touche est insérée en position 0
    // ce qui inverse l'ordre. On insère alors en fin de valeur.
    const supportsSelection = input.type !== 'number' && input.type !== 'email';
    const fallbackPos = input.value.length;
    const start = supportsSelection ? (input.selectionStart ?? fallbackPos) : fallbackPos;
    const end = supportsSelection ? (input.selectionEnd ?? fallbackPos) : fallbackPos;
    const currentValue = input.value;

    let newValue: string;
    let newCursorPos: number;

    if (key === 'BACKSPACE') {
      if (start !== end) {
        // Supprimer la sélection
        newValue = currentValue.slice(0, start) + currentValue.slice(end);
        newCursorPos = start;
      } else if (start > 0) {
        // Supprimer le caractère précédent
        newValue = currentValue.slice(0, start - 1) + currentValue.slice(start);
        newCursorPos = start - 1;
      } else {
        return;
      }
    } else if (key === 'ENTER') {
      if (input.tagName === 'TEXTAREA') {
        // Pour les textarea, insérer un retour à la ligne
        newValue = currentValue.slice(0, start) + '\n' + currentValue.slice(end);
        newCursorPos = start + 1;
      } else {
        // Pour les inputs, fermer le clavier et déclencher Enter
        closeKeyboard();
        const event = new KeyboardEvent('keydown', {
          key: 'Enter',
          code: 'Enter',
          keyCode: 13,
          which: 13,
          bubbles: true,
        });
        input.dispatchEvent(event);
        return;
      }
    } else if (key === 'SPACE') {
      newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
      newCursorPos = start + 1;
    } else if (key === 'CLEAR') {
      newValue = '';
      newCursorPos = 0;
    } else {
      // Cas spécial : si l'input est numérique et contient juste "0",
      // remplacer le zéro par le chiffre tapé au lieu de l'ajouter
      // (sinon l'utilisateur voit "05" au lieu de "5").
      const isNumericInput =
        input.type === 'number' ||
        input.getAttribute('inputMode') === 'numeric' ||
        input.getAttribute('inputMode') === 'decimal';
      const isDigit = /^[0-9]$/.test(key);
      if (isNumericInput && isDigit && currentValue === '0') {
        newValue = key;
        newCursorPos = 1;
      } else {
        // Ajouter le caractère à la position du curseur
        newValue = currentValue.slice(0, start) + key + currentValue.slice(end);
        newCursorPos = start + key.length;
      }
    }

    // Mettre à jour la valeur via un setter natif pour déclencher React
    const proto = input.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, newValue);
    }

    // Déclencher l'événement input pour React
    const inputEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(inputEvent);

    // Restaurer la position du curseur (setSelectionRange jette une exception
    // sur les inputs type=number et email dans WebKit — on l'ignore).
    requestAnimationFrame(() => {
      try {
        if (supportsSelection) {
          input.setSelectionRange(newCursorPos, newCursorPos);
        }
      } catch {
        // type=number / email : pas de selection range, on s'en passe
      }
      input.focus();
    });
  }, [closeKeyboard]);

  // Le clavier s'ouvre uniquement sur interaction explicite (clic / tap)
  // sur un input. On NE déclenche PAS sur focusin pour éviter qu'un focus
  // programmatique (ex: refocus de la barre de recherche après fermeture
  // d'un dialogue, autoFocus d'un input dans un dialog) ouvre le clavier
  // de manière intempestive.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Un appui commencé SUR le clavier (le glisser par sa barre de titre) qui se
    // termine ailleurs n'est pas un clic ailleurs : il ne doit pas le fermer.
    let appuiSurLeClavier = false;
    const handleMouseDown = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-virtual-keyboard]');
      appuiSurLeClavier = !!keyboardElement?.contains(e.target as Node);
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      if (appuiSurLeClavier) {
        appuiSurLeClavier = false;
        return;
      }

      const target = e.target as HTMLElement;
      const keyboardElement = document.querySelector('[data-virtual-keyboard]');

      // Si on clique sur le clavier, ne rien faire
      if (keyboardElement?.contains(target)) {
        return;
      }

      // Si on clique sur un autre input, le clavier se réouvrira via focusin
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Sinon fermer le clavier
      closeKeyboard();
    };

    // Helper partagé : ouvre le clavier si l'élément cliqué/tapé est un
    // input ou textarea acceptant la saisie.
    const tryOpenForTarget = (target: HTMLElement) => {
      if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
        return;
      }
      const input = target as HTMLInputElement | HTMLTextAreaElement;
      const type = input.getAttribute('type');
      if (type === 'hidden' || type === 'checkbox' || type === 'radio' || type === 'file' || type === 'submit' || type === 'button') {
        return;
      }
      if (input.hasAttribute('data-no-virtual-keyboard')) {
        return;
      }
      openKeyboard(input);
    };

    const handleTouchStart = (e: TouchEvent) => {
      tryOpenForTarget(e.target as HTMLElement);
    };

    const handleClick = (e: MouseEvent) => {
      tryOpenForTarget(e.target as HTMLElement);
    };

    // Fermeture au CLIC, plus à l'appui (21/10/2026) : à l'ouverture, la fenêtre
    // glisse ou la page se resserre pour faire place au clavier, et tout revient
    // en place à la fermeture. Fermé dès l'appui, le bouton visé (« Valider »…)
    // aurait bougé sous le doigt avant qu'on le relâche : le clic tombait à côté.
    // Au clic, le bouton a déjà reçu le sien quand la mise en page revient.
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('click', handleClick);
    };
  }, [enabled, isOpen, openKeyboard, closeKeyboard]);

  return (
    <VirtualKeyboardContext.Provider
      value={{
        isOpen,
        inputRef: inputRefState.current,
        position,
        inputType,
        openKeyboard,
        closeKeyboard,
        handleKey,
      }}
    >
      {children}
    </VirtualKeyboardContext.Provider>
  );
}
